// eslint-disable-next-line ts/ban-ts-comment
// @ts-ignore cache issues
import type { Cache } from "polystore-cache"

import * as comlink from "comlink"
import * as flatbuffers from "flatbuffers"

import type { InterfaceFile } from "~/classes/file/InterfaceFile"

import { DatagramBuf } from "~/classes/generated/datagram"
import { handleConversationCloseMessageDatagramBuf, handleConversationCloseResponseDatagramBuf } from "~/classes/handlers/conversation/close"
import { handleConversationCreateMessageDatagramBuf, handleConversationCreateResponseDatagramBuf } from "~/classes/handlers/conversation/create"
import { handleConversationDeleteMessageDatagramBuf, handleConversationDeleteResponseDatagramBuf } from "~/classes/handlers/conversation/delete"
import { handleConversationPropertiesResponseDatagramBuf } from "~/classes/handlers/conversation/properties"
import { handleConversationSubscribeResponseDatagramBuf } from "~/classes/handlers/conversation/subscribe"
import { handleConversationTurnMessageDatagramBuf, handleConversationTurnResponseDatagramBuf } from "~/classes/handlers/conversation/turn"
import { handleConversationUpdateMessageDatagramBuf, handleConversationUpdateResponseDatagramBuf } from "~/classes/handlers/conversation/update"
import { handleFolderContentHistoryResponseDatagramBuf } from "~/classes/handlers/file/content-history"
import { handleFileCopyResponseDatagramBuf } from "~/classes/handlers/file/copy"
import { handleFileCreateMessageDatagramBuf, handleFileCreateResponseDatagramBuf } from "~/classes/handlers/file/create"
import { handleFileDeleteMessageDatagramBuf, handleFileDeleteResponseDatagramBuf } from "~/classes/handlers/file/delete"
import { handleFileDownloadResponseDatagramBuf } from "~/classes/handlers/file/download"
import { handleFileEditMessageDatagramBuf, handleFileEditResponseDatagramBuf } from "~/classes/handlers/file/edit"
import { handleFileHistoryResponseDatagramBuf } from "~/classes/handlers/file/history"
import { handleFolderListResponseDatagramBuf } from "~/classes/handlers/file/list"
import { handleFileMoveMessageDatagramBuf, handleFileMoveResponseDatagramBuf } from "~/classes/handlers/file/move"
import { handleFilePrepareResponseDatagramBuf } from "~/classes/handlers/file/prepare"
import { handleFilePropertiesResponseDatagramBuf } from "~/classes/handlers/file/properties"
import { handleFileRevisionResponseDatagramBuf } from "~/classes/handlers/file/revision"
import { handleFolderSubscribeResponseDatagramBuf } from "~/classes/handlers/file/subscribe"
import { handleFileUpdateMessageDatagramBuf, handleFileUpdateResponseDatagramBuf } from "~/classes/handlers/file/update"
import { handleCopyFileFailedStatusDatagramBuf, handleCopyFileProgressStatusDatagramBuf, handleCopyFinishedStatusDatagramBuf, handleCopyStartedStatusDatagramBuf } from "~/classes/handlers/local/copy"
import { handleDownloadFileProgressStatusDatagramBuf, handleDownloadFinishedStatusDatagramBuf, handleDownloadProgressStatusDatagramBuf, handleDownloadStartedStatusDatagramBuf } from "~/classes/handlers/local/download"
import { sendLocalGuestLoginRequest } from "~/classes/handlers/local/guest-login"
import { sendLocalGuestLogoutRequest } from "~/classes/handlers/local/guest-logout"
import { sendLocalLoginRequest } from "~/classes/handlers/local/login"
import { sendLocalLogoutRequest } from "~/classes/handlers/local/logout"
import { handleUploadFileFailedStatusDatagramBuf, handleUploadFileProgressStatusDatagramBuf, handleUploadFinishedStatusDatagramBuf, handleUploadStartedStatusDatagramBuf } from "~/classes/handlers/local/upload"
import { handleSearchAutocompleteResponseDatagramBuf } from "~/classes/handlers/search/autocomplete"
import { handleSearchListResponseDatagramBuf } from "~/classes/handlers/search/list"
import { handleSearchUpdateResponseDatagramBuf } from "~/classes/handlers/search/update"
import { handleSharedFolderCreateMessageDatagramBuf, handleSharedFolderCreateResponseDatagramBuf } from "~/classes/handlers/sharedFolder/create"
import { handleSharedFolderFindResponseDatagramBuf } from "~/classes/handlers/sharedFolder/find"
import { handleSharedFolderMembershipMessageDatagramBuf, handleSharedFolderMembershipResponseDatagramBuf } from "~/classes/handlers/sharedFolder/membership"
import { handleDidConnectAnonStatusDatagramBuf } from "~/classes/handlers/status/connect-anon"
import { handleDidConnectAuthStatusDatagramBuf } from "~/classes/handlers/status/connect-auth"
import { handleDidDisconnectAnonStatusDatagramBuf } from "~/classes/handlers/status/disconnect-anon"
import { handleDidDisconnectAuthStatusDatagramBuf } from "~/classes/handlers/status/disconnect-auth"
import { handleWentOfflineAnonStatusDatagramBuf } from "~/classes/handlers/status/went-offline-anon"
import { handleWentOfflineAuthStatusDatagramBuf } from "~/classes/handlers/status/went-offline-auth"
import { handleWentOnlineAnonStatusDatagramBuf } from "~/classes/handlers/status/went-online-anon"
import { handleWentOnlineAuthStatusDatagramBuf } from "~/classes/handlers/status/went-online-auth"
import { handleSelfConversationsResponseDatagramBuf } from "~/classes/handlers/user/conversations"
import { handleUserFindResponseDatagramBuf } from "~/classes/handlers/user/find"
import { handleSelfInfoResponseDatagramBuf } from "~/classes/handlers/user/info"
import { handleSelfSharedFoldersResponseDatagramBuf } from "~/classes/handlers/user/shared-folders"
import { handleSelfSidebarResponseDatagramBuf } from "~/classes/handlers/user/sidebar"
import { handleSelfUpdateMessageDatagramBuf, handleSelfUpdateResponseDatagramBuf } from "~/classes/handlers/user/update"
import CacheWorker from "~/workers/cache.shared.worker?sharedworker"
import DatabaseWorker from "~/workers/database.shared.worker?sharedworker"

// TODO: separate internal vs public state
interface State {
  initializedCacheWorker?: Promise<void>
  nextReconnectDate?: Date
  status: CacheState["status"]
  epochId?: number
  userId?: string
  anonId?: string
}

/** deprecated */
export type WebUploadItem = {
  preHash: string
  fileId: string
  parentId: string
  name: string
  file?: File
}

let state: Ref<State>
let cache: comlink.Remote<Cache>
let cachePort: MessagePort

const console = useLogger("use-cache", theme.colors.teal.hex)

function defaultState(): State {
  return {
    status: "uninitialized",
  }
}
function clearState() {
  state.value = defaultState()
}

/**
 * Formats the API url that is used by `$fetch`. For most app stuff, this should be set
 * using the baseUrl construct from nuxt. However, for our tests, we have to use a
 * prefixed url since vitest doesn't follow nuxt's URL guidelines.
 * TODO: should the base url be configurable?
 */
function api(request: string) {
  if (isTesting()) {
    return `http://localhost:8080/api${request}`
  }
  return `/api${request}`
}

async function initCacheWorker(options = { mock: false }) {
  const { createDatagramChannel } = useTauri()
  if (state.value.initializedCacheWorker)
    throw new Error("Cache worker initialization already started")

  // On desktop, we don't create a background worker and instead forward
  // all messages to our tauri handler.
  // TODO: this currently serializes to json rather than using bytes,
  // so the web version might even be faster than desktop right now?
  if (isDesktop()) {
    state.value.userId = undefined
    state.value.anonId = undefined
    state.value = {
      ...state.value,
      ...await createDatagramChannel(datagram => onWorkerMessageDatagramBuf(datagram)),
    }
    return
  }

  // If we are in our mock cache we have other ways of settings things up
  if (options.mock) {
    const mocks = useMocks()
    mocks.cachePort!.addEventListener("message", e => onWebWorkerMessage(e))
    const dummyChannel = new MessageChannel()
    await mocks.cache!.addDatabaseChannel(comlink.transfer(dummyChannel.port1, [dummyChannel.port1]))
    return
  }

  const cacheWorker = new CacheWorker({ name: "Cache Manager" })
  cachePort = cacheWorker.port
  cache = comlink.wrap(cachePort)
  cachePort.addEventListener("message", e => onWebWorkerMessage(e))

  // Now we set up the database worker, which is a separate shared worker
  // that contains a database handle
  const dbWorker = new DatabaseWorker({ name: "Database Worker" })
  const details = await cache.addDatabaseChannel(comlink.transfer(dbWorker.port, [dbWorker.port]))
  state.value.epochId = details.epochId
  state.value.anonId = details.anonId
  state.value.userId = details.userId
  state.value.status = details.status
  console.debug("Cache web worker initialized", state.value)
}

async function waitForCacheWorkerToBeSetUp(options = { mock: false }) {
  if (!isDefined(state.value.initializedCacheWorker))
    state.value.initializedCacheWorker = initCacheWorker(options)
  return await state.value.initializedCacheWorker
}

function cacheWorker() {
  if (isTesting())
    return useMocks().cache
  if (!cache)
    throw new Error("Cache worker unavailable")
  return cache
}

function epochId() {
  return state.value.epochId
}

/** The cache being ready signifies that we are connected & online */
function isReady() {
  return ["readyAuth", "readyAnon"].includes(state.value.status)
}

/** The cache being offline means we are not ready, i.e. the socket is currently disconnected */
function isOffline() {
  return !isReady()
}

/**
 * Not whether we are connected to the server, simply whether we are not anonymous.
 */
function isAuthenticated() {
  return ["readyAuth", "pendingAuth"].includes(state.value.status)
}

function isAnonymous() {
  return ["readyAnon", "pendingAnon"].includes(state.value.status)
}

function isEpochIdFresh(epochId?: number) {
  if (!isDefined(epochId))
    return false
  return isReady() && state.value.epochId === epochId
}

function isFileFresh(file: InterfaceFile) {
  return isReady() && file.isCurrentAsOfEpoch(state.value.epochId)
}

function isConversationFresh(conversation: InterfaceConversation) {
  return isReady() && conversation.isCurrentAsOfEpoch(state.value.epochId)
}

/**
 * Web worker (non-desktop environment) handler / listener.
 * NOTE: because this is an event handler, errors thrown by this get lost
 * in the nether world!
 * @param e the event from the web worker channel
 */
function onWebWorkerMessage(e: MessageEvent) {
  if (isComlinkMessageEvent(e))
    // console.debug('Comlink message received', message.value)
    return

  try {
    onWorkerMessageDatagramBuf(e.data)
  }
  catch (error) {
    console.error("Error in onWebWorkerMessage", error)
    throw error
  }
}

async function uploadFromFilePicker(folder?: InterfaceFile) {
  const { currentRootFolder } = useNavigation()
  const { onWebFilesDropped } = useJobs()
  const files = await addFileToSelectionsWithFilePicker()
  const destination = folder ?? currentRootFolder.value
  if (!destination)
    return console.error("No destination folder")

  const transfer = new window.DataTransfer()
  Array.from(files).forEach(file => transfer.items.add(file))
  return onWebFilesDropped(
    transfer,
    destination as InterfaceFile,
  )
}

async function addFileToSelectionsWithFilePicker() {
  return new Promise<FileList>((resolve, reject) => {
    const input = document.createElement("input")
    input.type = "file"
    input.multiple = true
    input.onchange = () => resolve(input.files!)
    input.addEventListener("cancel", () => reject(new Error("File picker dismissed")))
    input.click()
  })
}

async function createSharedFolderWithDefaultProperties(
  sharedFolderPreHash: string,
  homePreHash: string,
  archivePreHash: string,
  ownerId: string,
  name: string,
  handle: string,
) {
  if (isDesktop()) {
    return await useTauri().createSharedFolderWithDefaultProperties(
      sharedFolderPreHash,
      homePreHash,
      archivePreHash,
      ownerId,
      name,
      handle,
    )
  }
  else if (isTesting()) {
    const mocks = useMocks()
    return await mocks.cache!.createSharedFolderWithDefaultProperties(
      sharedFolderPreHash,
      homePreHash,
      archivePreHash,
      ownerId,
      name,
      handle,
    )
  }
  return await cacheWorker().createSharedFolderWithDefaultProperties(
    sharedFolderPreHash,
    homePreHash,
    archivePreHash,
    ownerId,
    name,
    handle,
  )
}

async function createOnboardingContent() {
  if (!isAuthenticated())
    throw new Error("Cannot create onboarding content when not authenticated")
  return await cacheWorker().createOnboardingContent()
}

/**
 * If it's a "client did connect" message, then the socket is connected, let's
 * mark the client as connected. If the message is a user / login response, let's
 * send it to the useUser. If the message is a file response / message, send to
 * file system. If we get a socket closed event, update our state, and then try
 * to intelligently log back in.
 */
function onWorkerMessageDatagramBuf(datagram: Uint8Array) {
  // TODO: in-progress migration of datagram stuff here
  const byteBuf = new flatbuffers.ByteBuffer(datagram)
  const datagramBuf = DatagramBuf.getRootAsDatagramBuf(byteBuf)
  if (datagramBuf.responseLength()) {
    const responses = []
    for (let i = 0; i < datagramBuf.responseLength(); i++)
      responses.push(datagramBuf.response(i)!)
    for (const response of responses) {
      if (response.conversationCloseResponse())
        handleConversationCloseResponseDatagramBuf(response.conversationCloseResponse()!)
      else if (response.conversationCreateResponse())
        handleConversationCreateResponseDatagramBuf(response.conversationCreateResponse()!)
      else if (response.conversationDeleteResponse())
        handleConversationDeleteResponseDatagramBuf(response.conversationDeleteResponse()!)
      else if (response.conversationPropertiesResponse())
        handleConversationPropertiesResponseDatagramBuf(response.conversationPropertiesResponse()!)
      else if (response.conversationSubscribeResponse())
        handleConversationSubscribeResponseDatagramBuf(response.conversationSubscribeResponse()!)
      else if (response.conversationTurnResponse())
        handleConversationTurnResponseDatagramBuf(response.conversationTurnResponse()!)
      else if (response.conversationUpdateResponse())
        handleConversationUpdateResponseDatagramBuf(response.conversationUpdateResponse()!)
      else if (response.folderListPageResponse())
        handleFolderListResponseDatagramBuf(response.folderListPageResponse()!)
      else if (response.conversationPropertiesResponse())
        handleConversationPropertiesResponseDatagramBuf(response.conversationPropertiesResponse()!)
      else if (response.fileCopyResponse())
        handleFileCopyResponseDatagramBuf(response.fileCopyResponse()!)
      else if (response.fileCreateResponse())
        handleFileCreateResponseDatagramBuf(response.fileCreateResponse()!)
      else if (response.fileDeleteResponse())
        handleFileDeleteResponseDatagramBuf(response.fileDeleteResponse()!)
      else if (response.fileDownloadResponse())
        handleFileDownloadResponseDatagramBuf(response.fileDownloadResponse()!)
      else if (response.fileHistoryResponse())
        handleFileHistoryResponseDatagramBuf(response.fileHistoryResponse()!)
      else if (response.filePrepareResponse())
        handleFilePrepareResponseDatagramBuf(response.filePrepareResponse()!)
      else if (response.folderSubscribeResponse())
        handleFolderSubscribeResponseDatagramBuf(response.folderSubscribeResponse()!)
      else if (response.fileMoveResponse())
        handleFileMoveResponseDatagramBuf(response.fileMoveResponse()!)
      else if (response.filePropertiesResponse())
        handleFilePropertiesResponseDatagramBuf(response.filePropertiesResponse()!)
      else if (response.fileRevisionResponse())
        handleFileRevisionResponseDatagramBuf(response.fileRevisionResponse()!)
      else if (response.fileUpdateResponse())
        handleFileUpdateResponseDatagramBuf(response.fileUpdateResponse()!)
      else if (response.fileEditResponse())
        handleFileEditResponseDatagramBuf(response.fileEditResponse()!)
      else if (response.folderContentHistoryResponse())
        handleFolderContentHistoryResponseDatagramBuf(response.folderContentHistoryResponse()!)
      else if (response.searchAutocompleteResponse())
        handleSearchAutocompleteResponseDatagramBuf(response.searchAutocompleteResponse()!)
      else if (response.searchListPageResponse())
        handleSearchListResponseDatagramBuf(response.searchListPageResponse()!)
      else if (response.searchUpdateResponse())
        handleSearchUpdateResponseDatagramBuf(response.searchUpdateResponse()!)
      else if (response.selfInfoResponse())
        handleSelfInfoResponseDatagramBuf(response.selfInfoResponse()!)
      else if (response.selfConversationsResponse())
        handleSelfConversationsResponseDatagramBuf(response.selfConversationsResponse()!)
      else if (response.selfSharedFoldersResponse())
        handleSelfSharedFoldersResponseDatagramBuf(response.selfSharedFoldersResponse()!)
      else if (response.selfSidebarResponse())
        handleSelfSidebarResponseDatagramBuf(response.selfSidebarResponse()!)
      else if (response.selfUpdateResponse())
        handleSelfUpdateResponseDatagramBuf(response.selfUpdateResponse()!)
      else if (response.sharedFolderCreateResponse())
        handleSharedFolderCreateResponseDatagramBuf(response.sharedFolderCreateResponse()!)
      else if (response.userFindResponse())
        handleUserFindResponseDatagramBuf(response.userFindResponse()!)
      else if (response.sharedFolderFindResponse())
        handleSharedFolderFindResponseDatagramBuf(response.sharedFolderFindResponse()!)
      else if (response.sharedFolderMembershipResponse())
        handleSharedFolderMembershipResponseDatagramBuf(response.sharedFolderMembershipResponse()!)
      else
        console.error("Unknown response type found", response)
    }
  }
  else if (datagramBuf.messageLength()) {
    const messages = []
    for (let i = 0; i < datagramBuf.messageLength(); i++)
      messages.push(datagramBuf.message(i)!)
    for (const message of messages) {
      if (message.conversationCloseMessage())
        handleConversationCloseMessageDatagramBuf(message.conversationCloseMessage()!)
      else if (message.conversationCreateMessage())
        handleConversationCreateMessageDatagramBuf(message.conversationCreateMessage()!)
      else if (message.conversationDeleteMessage())
        handleConversationDeleteMessageDatagramBuf(message.conversationDeleteMessage()!)
      else if (message.conversationTurnMessage())
        handleConversationTurnMessageDatagramBuf(message.conversationTurnMessage()!)
      else if (message.conversationUpdateMessage())
        handleConversationUpdateMessageDatagramBuf(message.conversationUpdateMessage()!)
      else if (message.fileCreateMessage())
        handleFileCreateMessageDatagramBuf(message.fileCreateMessage()!)
      else if (message.fileDeleteMessage())
        handleFileDeleteMessageDatagramBuf(message.fileDeleteMessage()!)
      else if (message.fileMoveMessage())
        handleFileMoveMessageDatagramBuf(message.fileMoveMessage()!)
      else if (message.fileUpdateMessage())
        handleFileUpdateMessageDatagramBuf(message.fileUpdateMessage()!)
      else if (message.fileEditMessage())
        handleFileEditMessageDatagramBuf(message.fileEditMessage()!)
      else if (message.selfUpdateMessage())
        handleSelfUpdateMessageDatagramBuf(message.selfUpdateMessage()!)
      else if (message.sharedFolderMembershipMessage())
        handleSharedFolderMembershipMessageDatagramBuf(message.sharedFolderMembershipMessage()!)
      else if (message.sharedFolderCreateMessage())
        handleSharedFolderCreateMessageDatagramBuf(message.sharedFolderCreateMessage()!)

      else console.error("Unknown message type found", message)
    }
  }
  else if (datagramBuf.statusLength()) {
    const statuses = []
    for (let i = 0; i < datagramBuf.statusLength(); i++)
      statuses.push(datagramBuf.status(i)!)
    for (const status of statuses) {
      if (status.didConnectAuthStatus())
        handleDidConnectAuthStatusDatagramBuf(status.didConnectAuthStatus()!)
      else if (status.didDisconnectAuthStatus())
        handleDidDisconnectAuthStatusDatagramBuf(status.didDisconnectAuthStatus()!)
      else if (status.wentOfflineAuthStatus())
        handleWentOfflineAuthStatusDatagramBuf(status.wentOfflineAuthStatus()!)
      else if (status.wentOnlineAuthStatus())
        handleWentOnlineAuthStatusDatagramBuf(status.wentOnlineAuthStatus()!)
      else if (status.didConnectAnonStatus())
        handleDidConnectAnonStatusDatagramBuf(status.didConnectAnonStatus()!)
      else if (status.didDisconnectAnonStatus())
        handleDidDisconnectAnonStatusDatagramBuf(status.didDisconnectAnonStatus()!)
      else if (status.wentOfflineAnonStatus())
        handleWentOfflineAnonStatusDatagramBuf(status.wentOfflineAnonStatus()!)
      else if (status.wentOnlineAnonStatus())
        handleWentOnlineAnonStatusDatagramBuf(status.wentOnlineAnonStatus()!)
      else if (status.uploadStartedStatus())
        handleUploadStartedStatusDatagramBuf(status.uploadStartedStatus()!)
      else if (status.uploadFileProgressStatus())
        handleUploadFileProgressStatusDatagramBuf(status.uploadFileProgressStatus()!)
      else if (status.uploadFileFailedStatus())
        handleUploadFileFailedStatusDatagramBuf(status.uploadFileFailedStatus()!)
      else if (status.uploadFinishedStatus())
        handleUploadFinishedStatusDatagramBuf(status.uploadFinishedStatus()!)
      else if (status.downloadStartedStatus())
        handleDownloadStartedStatusDatagramBuf(status.downloadStartedStatus()!)
      else if (status.downloadProgressStatus())
        handleDownloadProgressStatusDatagramBuf(status.downloadProgressStatus()!)
      else if (status.downloadFileProgressStatus())
        handleDownloadFileProgressStatusDatagramBuf(status.downloadFileProgressStatus()!)
      else if (status.downloadFinishedStatus())
        handleDownloadFinishedStatusDatagramBuf(status.downloadFinishedStatus()!)
      else if (status.copyStartedStatus())
        handleCopyStartedStatusDatagramBuf(status.copyStartedStatus()!)
      else if (status.copyFileProgressStatus())
        handleCopyFileProgressStatusDatagramBuf(status.copyFileProgressStatus()!)
      else if (status.copyFileFailedStatus())
        handleCopyFileFailedStatusDatagramBuf(status.copyFileFailedStatus()!)
      else if (status.copyFinishedStatus())
        handleCopyFinishedStatusDatagramBuf(status.copyFinishedStatus()!)
      else
        console.error("Unknown cache message received", status)
    }
  }
  else
    console.warn("Datagram was empty or unidentified", datagramBuf)
}

function onConnectedAuth(datagram: DidConnectAuthStatusDatagram) {
  state.value.epochId = datagram.epochId
  state.value.nextReconnectDate = undefined
  state.value.status = "readyAuth"
  state.value.userId = datagram.userId
}

function onConnectedAnon(datagram: DidConnectAnonStatusDatagram) {
  state.value.epochId = datagram.epochId
  state.value.nextReconnectDate = undefined
  state.value.status = "readyAnon"
  state.value.anonId = datagram.anonId
}

async function sendWorkerDatagramBuf(msg: Uint8Array) {
  const { postDatagramBuf } = useTauri()

  await waitForCacheWorkerToBeSetUp()

  if (isDesktop()) {
    return postDatagramBuf(msg)
  }

  if (isTesting()) {
    const mocks = useMocks()
    return await mocks.cache!.onClientMessage(msg)
  }

  return cache.onClientMessage(comlink.transfer(msg, [msg.buffer]))
}

// TODO: we need to err if the user is not using a modern browser with
// this api
function supportsFileSystemAccessAPI() {
  return "getAsFileSystemHandle" in DataTransferItem.prototype
}

// TODO: we need to err if the user is not using a modern browser with
// this api
function supportsWebkitGetAsEntry() {
  return "webkitGetAsEntry" in DataTransferItem.prototype
}

function sendCacheLogin(userId: string) {
  if (!isDefined(userId))
    throw new Error("No user id")
  return sendLocalLoginRequest(userId)
}

function sendCacheLogout(userId: string) {
  if (!isDefined(userId))
    throw new Error("No user id")
  return sendLocalLogoutRequest(userId)
}

function sendCacheGuestLogin(anonId: string) {
  if (!isDefined(anonId))
    throw new Error("No anon id")
  return sendLocalGuestLoginRequest(anonId)
}

function sendCacheGuestLogout(anonId: string) {
  if (!isDefined(anonId))
    throw new Error("No anon id")
  return sendLocalGuestLogoutRequest(anonId)
}

async function sendUndoCommand() {
  await cacheWorker().undoLastAction()
}

async function sendRedoCommand() {
  await cacheWorker().redoLastAction()
}

/**
 * The useCache composable is the "core" composable that handles startup and
 * manages the cache background worker. We handle all responses from the websocket,
 * and pass them to the relevant part of the application.
 */
export default function () {
  state = useState<State>("cache-state", defaultState)

  return {
    cacheState: state,
    clearState,
    cacheWorker,
    epochId,
    isServer,
    isClient,
    isDesktop,
    isBrowser,
    isReady,
    isOffline,
    isAuthenticated,
    isAnonymous,
    api,
    sendCacheLogin,
    sendCacheLogout,
    sendCacheGuestLogin,
    sendCacheGuestLogout,
    waitForCacheWorkerToBeSetUp,
    isEpochIdFresh,
    isFileFresh,
    isConversationFresh,
    onWebWorkerMessage,
    onWorkerMessageDatagramBuf,
    onConnectedAuth,
    onConnectedAnon,
    supportsFileSystemAccessAPI,
    supportsWebkitGetAsEntry,
    sendWorkerDatagramBuf,
    uploadFromFilePicker,
    createSharedFolderWithDefaultProperties,
    sendUndoCommand,
    sendRedoCommand,
    createOnboardingContent,
  }
}
